import { ChangeDetectionStrategy, Component, input, output } from "@angular/core";
import { S25IconComponent } from "../../s25-icon/s25.icon.component";
import { S25ContactProfileImageComponent } from "../../student-engagement/contact/contact.profile.image.component";
import { chatRequest, DiagramChatRequest, DiagramPersonRow, DiagramSpawnedGroup } from "./workflow.diagram.model";
import { WorkflowDiagramPersonComponent } from "./workflow.diagram.person.component";

/** Up to three spawned-form rows on a node, then "+N more"; any row opens the full list in the detail pane. */
@Component({
    selector: "workflow-diagram-spawned-rows",
    template: `
        @let spawned = group();
        @if (!spawned.forms.length) {
            <div class="spawned-empty">No forms spawned yet</div>
        } @else {
            @for (form of spawned.visibleForms; track form.key) {
                <div class="spawned-row">
                    <button
                        type="button"
                        class="spawned-button"
                        [attr.aria-controls]="paneId()"
                        (click)="open.emit($any($event.currentTarget))"
                    >
                        <s25-ng-icon
                            class="spawned-marker"
                            [attr.data-submitted]="form.submitted"
                            [type]="form.submitted ? 'check' : 'clock'"
                            [ariaLabel]="false"
                        />
                        <span class="spawned-name">{{ form.name }}</span>
                        <span class="visuallyhidden">
                            , {{ form.submitted ? "Submitted" : "Not submitted"
                            }}{{ form.assignee ? ", assigned to " + form.assignee.name : "" }}
                        </span>
                    </button>
                    @if (form.assignee; as assignee) {
                        @if (assignee.restricted) {
                            <span class="assignee-placeholder" aria-hidden="true">
                                <s25-ng-icon [type]="'user'" [ariaLabel]="false" />
                            </span>
                        } @else {
                            <s25-ng-contact-profile-image
                                class="assignee-avatar"
                                [contactId]="assignee.contId"
                                [name]="assignee.name"
                                [size]="22"
                                aria-hidden="true"
                            />
                        }
                        <workflow-diagram-person
                            class="assignee-chat"
                            [person]="assignee"
                            [chatEnabled]="chatEnabled()"
                            [chatOnly]="true"
                            [onDark]="true"
                            (chat)="chatAssignee(assignee, $event)"
                        />
                    }
                </div>
            }
            @if (spawned.hiddenCount) {
                <button
                    type="button"
                    class="spawned-more"
                    [attr.aria-controls]="paneId()"
                    (click)="more.emit($any($event.currentTarget))"
                >
                    +{{ spawned.hiddenCount }} more
                    <span class="visuallyhidden">spawned forms</span>
                </button>
            }
        }
    `,
    host: { class: "workflow-diagram-spawned-rows" },
    styles: `
        :host {
            display: block;
        }

        .spawned-empty {
            height: 24px;
            line-height: 24px;
            padding-left: 28px;
            font-size: 12px;
            font-style: italic;
            color: rgba(255, 255, 255, 0.92);
        }

        .spawned-row {
            height: 30px;
            display: flex;
            align-items: center;
            gap: 4px;
            padding-left: 16px;
            box-sizing: border-box;
        }

        .spawned-button {
            flex: 1;
            min-width: 0;
            height: 26px;
            display: flex;
            align-items: center;
            gap: 6px;
            padding: 0 4px;
            border: none;
            border-radius: 4px;
            background: transparent;
            color: #fff;
            font: inherit;
            font-size: 12px;
            text-align: left;
            cursor: pointer;
        }

        .spawned-button:hover {
            background: rgba(255, 255, 255, 0.15);
        }

        .spawned-button:focus-visible,
        .spawned-more:focus-visible {
            outline: 2px solid #fff;
            outline-offset: 1px;
        }

        .spawned-marker {
            --icon-size: 14px;
            flex-shrink: 0;
        }

        .spawned-name {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        .assignee-avatar,
        .assignee-placeholder {
            flex-shrink: 0;
        }

        .assignee-placeholder {
            width: 22px;
            height: 22px;
            border-radius: 50%;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            background: rgba(255, 255, 255, 0.25);
            color: #fff;
            --icon-size: 14px;
        }

        .assignee-chat {
            flex-shrink: 0;
        }

        .spawned-more {
            display: block;
            height: 24px;
            margin: 2px 0 2px 36px;
            padding: 0 8px;
            border: 1px solid rgba(255, 255, 255, 0.6);
            border-radius: 12px;
            background: transparent;
            color: #fff;
            font: inherit;
            font-size: 12px;
            font-weight: 600;
            cursor: pointer;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [S25IconComponent, S25ContactProfileImageComponent, WorkflowDiagramPersonComponent],
})
export class WorkflowDiagramSpawnedRowsComponent {
    group = input.required<DiagramSpawnedGroup>();
    paneId = input<string>();
    chatEnabled = input(false);

    open = output<HTMLElement>();
    more = output<HTMLElement>();
    chatWith = output<DiagramChatRequest>();

    protected chatAssignee(assignee: DiagramPersonRow, trigger: HTMLElement) {
        const request = chatRequest(assignee, trigger);
        if (request) this.chatWith.emit(request);
    }
}
